import { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ArrowLeft, Calendar, FileText, Zap, Check, ChevronRight, Package } from 'lucide-react';

import PlanDisplay from "../../molecules/PlanDisplay/view";
import ElectronicDocumentConsumption from "../../organisms/ElectronicDocumentConsumption/view";
import { roleContext } from '../../../context/role/context';
import { upgradeSubscription } from "../../../usecases/subscriptions-usecase";
import { getMarketplacePackages, purchaseMarketplacePackage } from '../../../usecases/marketplace-usecase';
import { dispatchCustomEvent } from "../../../utils/dispatch";
import './style.css';
import ButtonPrimary from "../../atoms/ButtonPrimary/view";

function Planes() {
    const { profile, suscripcion } = useContext(roleContext);
    const [packages, setPackages] = useState([]);
    const [buyingId, setBuyingId] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [changingPlan, setChangingPlan] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const currentPlan = suscripcion?.plan || suscripcion;
    const planName = currentPlan && currentPlan !== 'no_suscription'
        ? (currentPlan.name || 'Suscripción Activa')
        : 'Sin Suscripción';
    const hasActivePlan = suscripcion && suscripcion.plan && suscripcion.plan.name !== 'no_suscription';

    const resolvePlan = () => {
        if (profile?.plan) return profile.plan;
        if (suscripcion?.plan) return suscripcion.plan;
        return suscripcion;
    };

    useEffect(() => {
        getMarketplacePackages()
            .then((res) => {
                const list = res?.data?.data || res?.data || [];
                setPackages(list);
            })
            .catch(() => setPackages([]));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('payment_status') || params.get('status') || params.get('collection_status');

        if (!status) return;

        const statusConfig = {
            approved: {
                icon: 'success',
                title: '¡Pago Aprobado!',
                text: 'Tu pago fue procesado exitosamente. Los documentos pueden tardar unos momentos en reflejarse en tu cuenta.',
            },
            rejected: {
                icon: 'error',
                title: 'Pago Rechazado',
                text: 'El pago fue rechazado. Intenta nuevamente o usa otro método de pago.',
            },
            pending: {
                icon: 'info',
                title: 'Pago Pendiente',
                text: 'Tu pago está siendo procesado. Puede tardar unos minutos en confirmarse. Te notificaremos cuando se complete.',
            },
            in_process: {
                icon: 'info',
                title: 'Pago en Proceso',
                text: 'Tu pago está siendo procesado. Puede tardar unos minutos en confirmarse. Te notificaremos cuando se complete.',
            },
        };

        const config = statusConfig[status] || statusConfig.pending;

        Swal.fire({
            icon: config.icon,
            title: config.title,
            text: config.text,
            confirmButtonText: 'Entendido',
        }).then(() => {
            navigate('/planes', { replace: true });
            if (status === 'approved') {
                dispatchCustomEvent('update-subscription', null);
            }
        });
    }, []);

    const handleBuyPackage = async (pkg) => {
        if (buyingId) return;
        setBuyingId(pkg.id);
        try {
            const result = await purchaseMarketplacePackage(Number(pkg.id));
            const responseData = result?.data;

            if (responseData?.init_point) {
                // Checkout Pro: redirigir al usuario a MercadoPago
                dispatchCustomEvent('popup', {
                    message: 'Serás redirigido a MercadoPago para completar tu pago.',
                    type: 'info'
                });
                setTimeout(() => {
                    window.location.href = responseData.init_point;
                }, 800);
                return;
            } else if (responseData?.id) {
                // skip_payment = true (dev/testing): paquete creado directamente
                dispatchCustomEvent('popup', {
                    message: `¡${pkg.name} adquirido con éxito!`,
                    type: 'success'
                });
                dispatchCustomEvent('update-subscription', null);
            } else {
                dispatchCustomEvent('popup', {
                    message: 'Error al adquirir el paquete. Respuesta inesperada.',
                    type: 'error'
                });
            }
        } catch {
            dispatchCustomEvent('popup', {
                message: 'Error al adquirir el paquete. Intenta de nuevo.',
                type: 'error'
            });
        } finally {
            setBuyingId(null);
        }
    };

    async function handleSelectPlan(plan) {
        if (!plan) return;

        const hasPaymentMethod = suscripcion?.aditional_info?.customer_id;

        if (hasActivePlan && hasPaymentMethod) {
            const isLowerTier = plan.hierarchy <= (currentPlan?.hierarchy || 0);
            if (isLowerTier) {
                const confirmed = window.confirm(
                    "Al cambiar a un plan inferior, algunos límites (usuarios, sucursales, documentos) se reducirán. ¿Quieres continuar?"
                );
                if (!confirmed) return;
            }

            setChangingPlan(true);
            try {
                dispatchCustomEvent('popup', { message: 'Procesando cambio de plan...', type: 'info' });
                await upgradeSubscription(plan.id);
                dispatchCustomEvent('popup', { message: '¡Plan actualizado con éxito!', type: 'success' });
                dispatchCustomEvent('update-subscription', null);
            } catch (error) {
                dispatchCustomEvent('popup', { message: 'Error al cambiar el plan. Asegúrate de tener un medio de pago registrado.', type: 'error' });
            } finally {
                setChangingPlan(false);
            }
            return;
        }

        setSelectedPlan(plan);
    }

    const currentPlanData = resolvePlan();

    return (
        <div className="planes-root">
            <div className="planes-unified">
                <button onClick={() => navigate('/profile')} className="planes-back-btn">
                    <ArrowLeft size={16} /> Volver al Perfil
                </button>

                <div className="planes-unified-header">
                    <h1 className="planes-title">Planes y Consumos</h1>
                    <p className="planes-subtitle">Gestiona tu plan de suscripción y adquiere paquetes adicionales</p>
                </div>

                {/* Sección 1: Plan Actual + Consumos */}
                <div className="planes-section">
                    <div className="planes-section-header">
                        <div className="planes-section-icon">
                            <Calendar size={22} />
                        </div>
                        <div>
                            <h2 className="planes-section-title">Plan Actual</h2>
                            <p className="planes-section-desc">{planName}</p>
                        </div>
                    </div>

                    {hasActivePlan && (
                        <div className="planes-consumption-wrapper">
                            <ElectronicDocumentConsumption />
                        </div>
                    )}

                    {!hasActivePlan && (
                        <div className="planes-no-plan">
                            <p>No tienes una suscripción activa. Elige un plan para empezar.</p>
                        </div>
                    )}
                </div>

                {/* Sección 2: Planes Disponibles */}
                <div className="planes-section">
                    <div className="planes-section-header">
                        <div className="planes-section-icon">
                            <Package size={22} />
                        </div>
                        <div>
                            <h2 className="planes-section-title">
                                {hasActivePlan ? 'Cambiar de Plan' : 'Planes Disponibles'}
                            </h2>
                            <p className="planes-section-desc">
                                {hasActivePlan
                                    ? 'Elige otro plan que se ajuste mejor a tu negocio'
                                    : 'Selecciona el plan que mejor se adapte a tu negocio'}
                            </p>
                        </div>
                    </div>

                    <div className="planes-grid-wrapper">
                        <PlanDisplay
                            onSelectPlan={handleSelectPlan}
                            currentPlan={currentPlanData}
                            roleId={profile?.role_id}
                        />
                    </div>

                    {selectedPlan && (
                        <div className="planes-activation-banner">
                            <p>
                                Has seleccionado <strong>{selectedPlan.name}</strong>. Completa el pago para activar tu suscripción.
                            </p>
                            <ButtonPrimary
                                onClick={async () => {
                                    try {
                                        if (!suscripcion?.aditional_info?.customer_id) {
                                            dispatchCustomEvent('popup', { message: 'Primero debes registrar un método de pago.', type: 'error' });
                                            return;
                                        }
                                        await upgradeSubscription(selectedPlan.id);
                                        dispatchCustomEvent('popup', { message: '¡Suscripción activada con éxito!', type: 'success' });
                                        dispatchCustomEvent('update-subscription', null);
                                        setSelectedPlan(null);
                                    } catch {
                                        dispatchCustomEvent('popup', { message: 'Error al activar la suscripción', type: 'error' });
                                    }
                                }}
                            >
                                Activar {selectedPlan.name} <ChevronRight size={16} />
                            </ButtonPrimary>
                        </div>
                    )}
                </div>

                {/* Sección 3: Paquetes de Documentos */}
                {packages.length > 0 && (
                    <div className="planes-section">
                        <div className="planes-section-header">
                            <div className="planes-section-icon">
                                <Zap size={22} />
                            </div>
                            <div>
                                <h2 className="planes-section-title">Paquetes de Documentos</h2>
                                <p className="planes-section-desc">Compra documentos electrónicos adicionales sin vencimiento</p>
                            </div>
                        </div>

                        <div className="planes-packages-grid">
                            {packages.map((pkg) => (
                                <div key={pkg.id} className="land-pagos planes-package-card">
                                    <div className="land-plan-inner">
                                        <p className="land-plan-title">{pkg.name}</p>
                                        <div className="planes-package-docs">
                                            <FileText size={18} />
                                            <span>{pkg.quantity} documentos</span>
                                        </div>
                                        <p className="land-plan-price" style={{ marginTop: 8 }}>
                                            <span className="land-plan-currency">$</span>
                                            <span className="land-plan-amount" style={{ fontSize: '2.6rem' }}>
                                                {Number(pkg.price).toLocaleString('es-CO')}
                                            </span>
                                        </p>
                                        <p className="land-plan-cycle">pago único</p>
                                    </div>

                                    <hr className="land-divider" />

                                    <div className="land-features-list">
                                        <p className="land-features-title">Incluye</p>
                                        <p className="land-plan-item">
                                            <Check size={16} className="land-plan-check" />
                                            {pkg.quantity} documentos electrónicos
                                        </p>
                                        <p className="land-plan-item">
                                            <Check size={16} className="land-plan-check" />
                                            Sin vencimiento
                                        </p>
                                    </div>

                                    <div className="land-btn-wrapper">
                                        <ButtonPrimary
                                            onClick={() => handleBuyPackage(pkg)}
                                            disabled={buyingId === pkg.id}
                                        >
                                            {buyingId === pkg.id ? 'Procesando...' : 'Comprar'}
                                            <ChevronRight size={16} />
                                        </ButtonPrimary>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Planes;
